
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        toast.error(error.message);
        throw error;
      }
      
      setEmailSent(true);
      toast.success("E-Mail zum Zurücksetzen des Passworts wurde gesendet!");
    } catch (error) {
      console.error("Password reset error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-uplivion-lightBlue/30 flex flex-col items-center justify-center p-4">
        <Link to="/" className="text-3xl font-bold text-uplivion-blue mb-8">
          uplivion
        </Link>
        
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8 text-center">
          <h1 className="text-2xl font-bold mb-6">E-Mail gesendet!</h1>
          
          <p className="text-gray-600 mb-6">
            Wir haben dir eine E-Mail mit einem Link zum Zurücksetzen deines Passworts an <strong>{email}</strong> gesendet.
          </p>
          
          <p className="text-sm text-gray-500 mb-6">
            Überprüfe auch deinen Spam-Ordner, falls du die E-Mail nicht in deinem Posteingang findest.
          </p>
          
          <Link 
            to="/login" 
            className="inline-flex items-center text-uplivion-blue hover:underline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 flex flex-col items-center justify-center p-4">
      <Link to="/" className="text-3xl font-bold text-uplivion-blue mb-8">
        uplivion
      </Link>
      
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Passwort vergessen?</h1>
        
        <p className="text-gray-600 text-center mb-6">
          Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen deines Passworts.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input 
              id="email" 
              type="email" 
              placeholder="deine@email.de" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required 
            />
          </div>
          
          <Button 
            type="submit" 
            className="w-full bg-uplivion-blue hover:bg-blue-700"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Lädt...
              </>
            ) : (
              "Passwort zurücksetzen"
            )}
          </Button>
        </form>
        
        <div className="mt-6 text-center">
          <Link 
            to="/login" 
            className="inline-flex items-center text-uplivion-blue hover:underline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
